import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { CodeBlock } from "../components/CodeBlock";
import { toast } from "sonner";
import { FolderSimple, Trash, FileCode, Terminal, Package, X, ClockCounterClockwise, ArrowUUpLeft, CheckCircle } from "@phosphor-icons/react";

const typeIcon = { script: Terminal, profile: FileCode, software: Package };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [verId, setVerId] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/projects")
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const open = (p) => {
    setActive(p);
    setVerId(p.current_version_id || p.versions?.[0]?.id || null);
  };

  const rollback = async (versionId) => {
    if (!active) return;
    setRollingBack(true);
    try {
      const { data } = await api.post(`/projects/${active.id}/rollback`, { version_id: versionId });
      setActive(data);
      setVerId(versionId);
      setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
      toast.success("Rolled back to selected version");
    } catch {
      toast.error("Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <FolderSimple size={22} className="text-[#00F0FF]" /> My Projects
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">Saved scripts and configuration profiles.</p>
      </div>

      <div className="p-8">
        {loading ? (
          <p className="font-mono text-sm text-[#71717A]">loading…</p>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-[#27272A] py-20 text-center">
            <FolderSimple size={40} className="text-[#27272A] mx-auto mb-4" />
            <p className="text-sm text-[#71717A] font-mono">
              No saved projects yet. Generate an artifact and hit Save.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const Icon = typeIcon[p.output_type] || Terminal;
              return (
                <div
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  onClick={() => open(p)}
                  className="border border-[#27272A] bg-[#121214] p-5 cursor-pointer hover:border-[#00F0FF] transition-colors duration-200 group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <Icon size={20} className="text-[#00F0FF]" />
                    <button
                      data-testid={`delete-project-${p.id}`}
                      onClick={(e) => remove(p.id, e)}
                      className="text-[#71717A] hover:text-[#FF5F56] transition-colors duration-200"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                  <h3 className="text-sm tracking-tight mb-1 truncate">{p.title}</h3>
                  <div className="flex items-center gap-2 font-mono text-[10px] text-[#71717A]">
                    <span className="text-[#00F0FF] uppercase">{p.provider}</span>
                    <span>·</span>
                    <span>{p.filename}</span>
                  </div>
                  <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#71717A] mt-2">
                    <ClockCounterClockwise size={12} />
                    {(p.versions?.length || 1)} version{(p.versions?.length || 1) > 1 ? "s" : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setActive(null)}
        >
          <div
            className="w-full max-w-5xl bg-[#0A0A0B] border border-[#27272A] max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="project-detail-modal"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-4 bg-[#0A0A0B]">
              <div>
                <h3 className="text-lg tracking-tight">{active.title}</h3>
                <p className="font-mono text-xs text-[#71717A] mt-0.5">
                  {active.provider.toUpperCase()} · {active.output_type}
                </p>
              </div>
              <button
                data-testid="close-modal-btn"
                onClick={() => setActive(null)}
                className="text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-1 min-h-0">
              {/* Version timeline */}
              <div className="w-64 shrink-0 border-r border-[#27272A] overflow-auto">
                <div className="px-4 py-3 border-b border-[#27272A] font-mono text-xs text-[#00F0FF] flex items-center gap-1.5">
                  <ClockCounterClockwise size={14} /> DEPLOY HISTORY
                </div>
                <div className="p-2" data-testid="version-timeline">
                  {[...(active.versions || [])].reverse().map((v, idx, arr) => {
                    const isActive = v.id === active.current_version_id;
                    const isViewing = v.id === verId;
                    const num = arr.length - idx;
                    return (
                      <div
                        key={v.id}
                        data-testid={`version-item-${v.id}`}
                        onClick={() => setVerId(v.id)}
                        className={`cursor-pointer p-3 border-l-2 mb-1 transition-colors duration-200 ${
                          isViewing ? "border-[#00F0FF] bg-[#121214]" : "border-transparent hover:bg-[#121214]"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-[#FAFAFA]">v{num}</span>
                          {isActive && (
                            <span className="flex items-center gap-1 font-mono text-[10px] text-[#00FF41]">
                              <CheckCircle size={12} weight="fill" /> live
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-[#71717A] mt-1 truncate">{v.note}</div>
                        <div className="font-mono text-[10px] text-[#52525b] mt-0.5">
                          {new Date(v.created_at).toLocaleString()}
                        </div>
                        {isViewing && !isActive && (
                          <button
                            data-testid={`rollback-${v.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              rollback(v.id);
                            }}
                            disabled={rollingBack}
                            className="mt-2 w-full flex items-center justify-center gap-1.5 border border-[#0A84FF] text-[#0A84FF] py-1.5 text-[11px] hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"
                          >
                            <ArrowUUpLeft size={12} /> {rollingBack ? "…" : "Roll back to this"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Version content */}
              <div className="flex-1 overflow-auto p-6 space-y-4">
                {(() => {
                  const shown = active.versions?.find((v) => v.id === verId) || active;
                  return (
                    <>
                      <CodeBlock code={shown.content} language={shown.language} filename={shown.filename} />
                      {shown.deploy_notes && (
                        <div className="border border-[#27272A] bg-[#121214] p-4">
                          <div className="font-mono text-xs text-[#00F0FF] mb-2">DEPLOYMENT NOTES</div>
                          <p className="text-sm text-[#A1A1AA] leading-relaxed">{shown.deploy_notes}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
